import type { MockupJob, MockupTemplate } from '@eip/engine';
import { hasWebGL2 } from '../lib/capabilities';
import type { GlPreviewState } from '../state/mockup';
import type { PreviewMaps } from '../worker/protocol';
import { createMockupGl, type MockupGl } from './gl';
import { glJobFor } from './gl-job';

/**
 * The one GPU mockup renderer the app has, and the memory of what is already uploaded to it.
 *
 * `MockupGl` deliberately remembers nothing between frames (see `gl.ts`), because Task 2 needed a
 * renderer a test could drive one frame at a time. A viewer painting a drag needs the opposite: the
 * five map textures and the photo cost ~10 ms to upload and must NOT be re-sent per frame, so
 * something has to know which template's maps and which art bitmaps the GPU is holding. That is all
 * this module is — a singleton renderer plus two pieces of bookkeeping.
 *
 * There is exactly one because there is exactly one canvas on screen: a second renderer would be a
 * second WebGL2 context (browsers cap those at ~16 and drop the oldest), a second copy of every map
 * and a second 9 MB drawing buffer, for a view the user cannot see.
 *
 * NO React and NO store in here. The hook owns when a frame is worth drawing and owns the
 * `glPreview` state the UI reads; this module owns the GL objects and answers one question per call.
 */

/** The canvas the GL frames land on. Never attached to the DOM — the viewer `drawImage`s it onto its
 *  own 2D canvas, scaled to the photo's size, exactly as it does the CPU preview bitmap — and never
 *  sized here: `setMaps` is what resizes it, to the maps it is uploading. */
let canvas: HTMLCanvasElement | null = null;
let gl: MockupGl | null = null;
/**
 * True once `createMockupGl` has refused: a browser that says it knows WebGL2 and then cannot give us
 * a working renderer (a shader that will not compile, a GPU process that died between `getContext`
 * and `link`, a context handed over already lost).
 *
 * A page-lifetime fact, NOT cleared by `glPreviewReset`, and that is load-bearing in two ways: a
 * renderer this browser has already refused to build once will not build on the next animation
 * frame either, and — because a refusal is reported through the loss callback, whose subscriber
 * resets us — clearing it there would put creation and reset in a loop.
 */
let failed = false;
/** True from the moment a context loss (or a refused creation) is reported until the next frame
 *  actually renders. Sticky across `glPreviewReset` so `glPreviewState` can still say WHY the CPU
 *  path is back. */
let lost = false;
/**
 * CONSECUTIVE context losses — the count is cleared by every frame that renders (see
 * `glPreviewRender`). A loss on its own is recoverable: the caller resets us, the next frame builds a
 * new context, and on a healthy machine nobody notices. What this bounds is a GPU that is *thrashing*
 * — losing the context again before it has drawn anything — which would otherwise have us build a
 * context per frame forever, since every loss asks for exactly one more repaint. Three of those in a
 * row and the GPU path is over for this page: the preview stays on the CPU, the banner stays up, and
 * a reload is the way back.
 *
 * Deliberately NOT cleared by `glPreviewReset`, which is called once per loss: a budget the thing
 * spending it can reset is not a budget. A successful render is the only thing that clears it,
 * because that is the only evidence that the new context actually works.
 */
let losses = 0;
const MAX_LOSSES = 3;
/**
 * TOTAL contexts built in this page's life, never reset by anything — not by a successful render and
 * not by `glPreviewReset`. The consecutive budget above cannot end the one failure mode that needs
 * no user input to keep going: a driver that grants a context, lets a frame render (clearing
 * `losses`) and then revokes it is a create → render → lose → repaint cycle with no exit, because
 * every loss asks for exactly one more frame. Twelve is well past anything a healthy session reaches
 * — a healthy session builds ONE — and past it the GPU path is over for the page in exactly the way
 * a spent `MAX_LOSSES` ends it: `glPreviewUnavailable()` is true, `lost` is still set from the loss
 * that spent the last one, so `glPreviewState()` says `'lost'` and the §8 banner stays up.
 */
let creations = 0;
const MAX_CREATIONS = 12;
/** So a throw out of GL is reported once rather than once per animation frame. */
let warned = false;
/** True while the current context has a frame in it — what makes `glPreviewState` say `'gl'`. */
let rendered = false;
/** `${templateId}:${prepGeneration}` of the maps uploaded to the current context, or null for none.
 *  The generation is half the key on purpose: re-preparing a template (a user blank re-saved from
 *  the editor) keeps its id and changes every map. */
let mapsKey: string | null = null;
/** Which bitmap each art id's texture was uploaded FROM, by object identity: `previews` replaces the
 *  bitmap wholesale when a run lands a new output, so identity is exactly the test for "re-upload". */
const arts = new Map<string, ImageBitmap>();
/** The same bookkeeping for the backdrop scene covers, keyed by `sceneCoverKey` and tracked by
 *  bitmap identity: `putSceneCover` replaces (and closes) the entry when a scene is re-imported at
 *  a size already cut, so identity is exactly the test for "re-upload". Bounded by the frame: a key
 *  the current job does not name is dropped, as an art nothing prints any more is. */
const covers = new Map<string, ImageBitmap>();
/** Context-loss subscribers. A Set rather than the renderer's own list because they outlive the
 *  renderer: the point of subscribing is to hear that THIS renderer is gone. */
const lostFns = new Set<() => void>();

/** The renderer, created on the first frame that wants one. Null — with no exception and no retry —
 *  when this browser cannot give us WebGL2. */
function ensureGl(): MockupGl | null {
  if (gl) return gl;
  if (glPreviewUnavailable()) return null;
  if (typeof document === 'undefined') return null;
  if (!canvas) canvas = document.createElement('canvas');
  let made: MockupGl | null = null;
  try {
    made = createMockupGl(canvas);
  } catch {
    // `createMockupGl` throws rather than returning null when a shader will not compile or link,
    // which is what a context that died between `getContext` and here looks like from in there.
    made = null;
  }
  if (!made) {
    // Set BEFORE the callback runs, both so a re-entrant `ensureGl` refuses and so the reset the
    // subscriber performs cannot re-arm what it is being told about.
    failed = true;
    lost = true;
    // The same news as a lost context, from the caller's point of view: the preview is on the CPU and
    // the user is owed the banner that says so. Reached at most once per page, because `failed` is a
    // page-lifetime fact — so this cannot become a per-frame notification.
    for (const fn of [...lostFns]) fn();
    return null;
  }
  gl = made;
  creations += 1;
  mapsKey = null;
  arts.clear();
  covers.clear();
  rendered = false;
  made.onContextLost(() => {
    lost = true;
    rendered = false;
    losses += 1;
    // A copy: a subscriber is entitled to unsubscribe (or to reset us) from inside its own callback.
    for (const fn of [...lostFns]) fn();
  });
  return gl;
}

/**
 * Draw one frame of `job` against `maps`, uploading whatever the GPU is not already holding, and
 * hand back the canvas to draw. Null when there is no GPU path right now — no WebGL2, a context
 * that would not come back, or a context lost inside this very call — and the caller shows the CPU
 * render instead.
 *
 * `arts` are the LIVE art bitmaps by id (`previews.get(docId)?.output?.bitmap`); the sizes the
 * shader indexes its art texture with are taken from the bitmaps themselves, never from a run's
 * recorded dimensions, because the texture is the bitmap.
 *
 * `sceneCovers` are the backdrop covers the caller has in hand, by `sceneCoverKey`
 * (`state/bitmaps.ts`'s `sceneCovers`). Only the keys passed here can be drawn: a scene backdrop
 * whose cover has not landed yet falls through to `backdrop.kind` 0, which is why the caller keeps
 * its GL frame off the screen until it has one (`useMockupView`).
 */
export function glPreviewRender(
  maps: PreviewMaps,
  template: MockupTemplate,
  job: MockupJob,
  artBitmaps: Record<string, ImageBitmap>,
  selfId: string,
  sceneCovers: Record<string, ImageBitmap> = {},
): HTMLCanvasElement | null {
  const r = ensureGl();
  if (!r) return null;
  try {
    const key = `${maps.templateId}:${maps.prepGeneration}`;
    if (mapsKey !== key) { r.setMaps(maps); mapsKey = key; }
    const sizes: Record<string, { width: number; height: number }> = {};
    for (const id of Object.keys(artBitmaps)) {
      const bitmap = artBitmaps[id];
      if (arts.get(id) !== bitmap) { r.setArt(id, bitmap); arts.set(id, bitmap); }
      // The uploaded texture's own dimensions, which is what `FRAG_PRINT` samples with.
      sizes[id] = { width: bitmap.width, height: bitmap.height };
    }
    // An art nothing prints any more is dropped rather than left parked: these are up to 2048² RGBA
    // (~16 MB of VRAM) each, and a print switched to another document would otherwise keep both.
    for (const id of [...arts.keys()]) if (!(id in artBitmaps)) { r.dropArt(id); arts.delete(id); }
    // The scene covers, on exactly the same terms as the arts above: uploaded when the bitmap the
    // caller holds is not the one this texture was made from, and dropped when the job no longer
    // names them — a cover is another full-size RGBA texture (up to ~9 MB), and a shop trying three
    // backdrops would otherwise leave all three on the GPU.
    for (const key of Object.keys(sceneCovers)) {
      const bitmap = sceneCovers[key];
      if (covers.get(key) !== bitmap) { r.setScene(key, bitmap); covers.set(key, bitmap); }
    }
    for (const key of [...covers.keys()]) if (!(key in sceneCovers)) { r.dropScene(key); covers.delete(key); }
    // Either the context died mid-frame (the loss callback has already fired) or the maps did not
    // take: forget it all and let whatever comes next re-send everything.
    if (!r.render(glJobFor(maps, template, job, sizes, selfId, new Set(covers.keys())))) return forget();
    rendered = true;
    lost = false;
    // This context has drawn a frame, so whatever it took to get here is spent history — the budget
    // exists to stop a GPU that never gets this far, not to ration a long healthy session.
    losses = 0;
    return r.canvas as HTMLCanvasElement;
  } catch (err) {
    // This whole function runs inside React's render pass, so a GL call that throws where nothing is
    // allowed to would take the app down rather than fall back. The documented candidate is
    // `texImage2D` with a DETACHED ImageBitmap (`InvalidStateError`); there is no live path to one
    // today, which is exactly why it must not be the thing that breaks the page tomorrow. Reported
    // once — silently swallowing it would make the CPU fallback impossible to explain in dev.
    if (!warned) {
      warned = true;
      console.warn('mockup gl: the GPU preview threw, falling back to the CPU render', err);
    }
    return forget();
  }
}

/** Drop every belief about what is uploaded, and report "no frame". The next call re-sends the maps
 *  and the arts from scratch, which is the only safe assumption after a failure inside GL. */
function forget(): null {
  rendered = false;
  mapsKey = null;
  arts.clear();
  covers.clear();
  return null;
}

/**
 * True when the GPU path is over for this page rather than merely off for this frame: no WebGL2 at
 * all, a renderer this browser refused to build, a GPU that kept losing its context before it could
 * draw, or one that has cost this page `MAX_CREATIONS` contexts however healthy each looked.
 * Everything that only exists to feed the GPU checks this first — notably the preview MAPS, which
 * cost a worker call and tens of MB per template and would never be drawn from.
 *
 * Deliberately not "is there a frame right now": a template whose maps have not landed yet is not a
 * browser without a GPU, and the two must not be confused.
 */
export function glPreviewUnavailable(): boolean {
  return !hasWebGL2() || failed || losses >= MAX_LOSSES || creations >= MAX_CREATIONS;
}

/** Which renderer the preview is on, from THIS module's point of view.
 *
 *  Part of the surface the brief asked this module to expose, and NOT what the UI reads: the store's
 *  `glPreview` is authoritative for the inspector row and the banner, because only the hook knows
 *  whether a frame was actually handed to the viewer. This is here for the test seam and for anything
 *  that wants the answer without a React render. */
export function glPreviewState(): GlPreviewState {
  if (!hasWebGL2()) return 'cpu';
  if (gl && rendered) return 'gl';
  if (lost) return 'lost';
  return 'cpu';
}

/** The last rendered frame as top-down RGBA, or null when there is no frame to read. */
export function glPreviewReadPixels(): Uint8ClampedArray | null {
  return gl && rendered ? gl.readPixels() : null;
}

/**
 * Hear about a context loss. Returns its own unsubscribe, so a React effect can be `() => on(fn)`.
 *
 * NOT replayed, unlike `gl.ts`'s own `onContextLost`: a subscriber that arrives after a loss (or
 * after a refused creation) has already been reported hears nothing, and would have to reconcile
 * from `glPreviewState()` instead. That is safe only because of the order the one consumer runs in —
 * the hook subscribes from an effect but renders from a memo that needs `maps`, and the only thing
 * that fetches maps is that same hook's own later effect, so the subscription always precedes the
 * first `glPreviewRender`. A second consumer, a warm-up fetch from the session, or a test that
 * renders before the viewer mounts would each break that ordering, and the fix is two lines here
 * (`if (failed || lost) fn();` before the `add`) plus a case in `gl-preview.test.ts`.
 */
export function onGlPreviewLost(fn: () => void): () => void {
  lostFns.add(fn);
  return () => { lostFns.delete(fn); };
}

/**
 * Throw the renderer away and allow a new one. Called after a context loss (the GL objects of a lost
 * context are all gone, so the only way back is a fresh renderer) and by the tests.
 *
 * The CANVAS goes too, not just the renderer: `getContext('webgl2')` on a canvas whose context was
 * lost hands the same context back, so reusing it would rebuild on top of the corpse. A new canvas
 * is the only reliable way to a new context — and nothing outside holds the old one for longer than
 * the frame it was drawn in.
 *
 * What it does NOT clear is the three page-lifetime facts, `failed`, `losses` and `creations`. This
 * is called BY the loss subscriber, so a reset that re-armed any of them would be a reset that
 * undoes the only guards against an endless rebuild — and a browser that has already refused to
 * build a renderer has not changed its mind because we asked again a frame later.
 */
export function glPreviewReset(): void {
  gl?.dispose();
  gl = null;
  canvas = null;
  mapsKey = null;
  arts.clear();
  covers.clear();
  rendered = false;
}

/**
 * Kill the context on purpose, for verifying the fallback by hand (`window.__eip.loseContext()`).
 * False when there is nothing to kill or the browser does not offer the extension.
 *
 * The context is fetched back off the canvas rather than held here: `getContext` on a canvas that
 * already has one returns that same context, so this needs nothing from `gl.ts`'s closure.
 */
export function glPreviewLoseContext(): boolean {
  if (!canvas || !gl) return false;
  const ctx = canvas.getContext('webgl2') as WebGL2RenderingContext | null;
  const ext = ctx?.getExtension('WEBGL_lose_context') as { loseContext(): void } | null | undefined;
  if (!ext) return false;
  ext.loseContext();
  return true;
}
